import { z } from 'zod';

/**
 * Nome de fuso IANA vindo de FORA (o `uiTimeZone` que o canal app manda a partir
 * do dispositivo, #3065). A validação é o próprio runtime: `Intl` lança
 * `RangeError` para qualquer nome que não seja um fuso real, então nada que não
 * seja fuso — inclusive prosa que tente virar instrução no prompt da recepção —
 * atravessa. A saída é a forma CANÔNICA que o runtime resolve (`america/sao_paulo`
 * → `America/Sao_Paulo`), sem tabela de fusos a manter. Vazio, longo demais ou
 * desconhecido ⇒ inválido; quem lê cai no fuso de operação.
 *
 * Única exceção: os apelidos de UTC. V8 (Node) resolve os links do IANA
 * (`GMT` → `UTC`), JavaScriptCore (Bun) devolve o link como veio (`GMT`) — o
 * mesmo dispositivo produziria fusos diferentes conforme o motor. A família UTC
 * vira `UTC` aqui; os demais links seguem o que o runtime resolver.
 */
const UTC_ALIASES = new Set([
  'UTC',
  'UCT',
  'GMT',
  'GMT0',
  'GMT+0',
  'GMT-0',
  'ZULU',
  'UNIVERSAL',
  'GREENWICH',
  'ETC/UTC',
  'ETC/UCT',
  'ETC/GMT',
  'ETC/GMT0',
  'ETC/GMT+0',
  'ETC/GMT-0',
  'ETC/ZULU',
  'ETC/UNIVERSAL',
  'ETC/GREENWICH',
]);

export const TimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .transform((value, ctx) => {
    try {
      const resolved = new Intl.DateTimeFormat('en', { timeZone: value }).resolvedOptions()
        .timeZone;
      return UTC_ALIASES.has(resolved.toUpperCase()) ? 'UTC' : resolved;
    } catch {
      ctx.addIssue({ code: 'custom', message: 'unknown IANA time zone' });
      return z.NEVER;
    }
  });
